// src/features/medicalSercices/page/SalesPersonForm.tsx

import { GENDER_OPTIONS } from '../../../../common/staticData/options';
import { API_ENDPOINTS, QUERY_KEYS } from '../../api/apiEndpoints';
import DynamicForm, { type DynamicFormSchema } from '@/common/components/DynamicForm/DynamicForm';
import { useTranslation } from 'react-i18next';

export default function SalesPersonForm() {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === 'ar';

    const SalesPersonFormSchema: DynamicFormSchema = {
        layout: 'vertical', // Overall form layout
        // submitButtonText: 'Save User', // Custom text for the submit button
        showResetButton: true, // Show a reset button

        // Define form sections for better organization and complex layouts
        sections: [
            {
                key: 'salesPerson-personal',
                title: 'Sales Person Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'name',
                        label: 'Name',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter a name.', // Custom required message
                        pattern: 'AlphabetsWithSpaceCommaHypeDot',
                        validation: [
                            { min: 3, message: t('Name must be at least 3 characters long.') },
                            { max: 100, message: t('Name cannot be more than 100 characters long.') },
                        ],
                        autocomplete: 'false',

                        colSpan: 24, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'dob',
                        label: 'Date of Birth',
                        type: 'date',
                        required: true,
                        requiredMessage: t('Please select a date of birth.'),
                        colSpan: 6,
                        validation: [
                            {
                                validator: (_: unknown, value: string) => {
                                    if (!value) return Promise.resolve();
                                    const selectedDate = new Date(value);
                                    const today = new Date();
                                    if (selectedDate > today) {
                                        return Promise.reject(new Error(t('Date of Birth cannot be in the future.')));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ],
                    },
                    {
                        name: 'gender',
                        label: 'Gender',
                        required: true, requiredMessage: 'Select gender',
                        type: 'select',
                        options: GENDER_OPTIONS,
                        colSpan: 6,
                    },
                    {
                        name: 'NationalIDNo',
                        label: 'National ID',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter a National ID No.', // Custom required message
                        pattern: 'AlphabetsWithSpaceCommaHypeDot',
                        autocomplete: 'false',

                        colSpan: 6, // Takes 12 out of 24 grid units (half width)
                    },
                    {
                        name: 'PassportNo',
                        label: 'Passport No',
                        type: 'input',
                        required: true,
                        requiredMessage: 'Please enter a Passport No.', // Custom required message
                        pattern: 'AlphabetsWithSpaceCommaHypeDot',
                        validation: [
                            { max: 15, message: t('National ID No cannot be more than 15 characters long.') },
                        ],
                        autocomplete: 'false',

                        colSpan: 6, // Takes 12 out of 24 grid units (half width)
                    }

                ],
            },
            {
                key: 'salesPerson-contact',
                title: 'Contact Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'mobileNo', label: 'Mobile Number', type: 'input', required: true, colSpan: 12,
                        pattern: 'PhoneNumber',
                        //validation: [
                        //    { max: 20, message: 'Mobile Number cannot be more than 20 characters long.' },
                        //],
                    },
                    {
                        name: 'phoneNo', label: 'Phone Number', type: 'input', colSpan: 12,
                        pattern: 'PhoneNumber',
                        //validation: [
                        //    { max: 20, message: 'Phone Number cannot be more than 20 characters long.' },
                        //],
                    },
                    {
                        name: 'faxNo', label: 'Office Fax', type: 'input', colSpan: 12,
                        pattern: 'PhoneNumber',
                        //validation: [
                        //    { max: 20, message: 'Office Fax Number cannot be more than 20 characters long.' },
                        //],
                    },
                    {
                        name: 'email', label: 'Office Email', type: 'input', colSpan: 12,
                        pattern: 'Email',
                        validation: [
                            { max: 100, message: t('Email cannot be more than 100 characters long.') },
                        ],
                    },

                ],
            },
            {
                key: 'salesPerson-address',
                title: 'Address Information',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'countryId',
                        label: 'Country',
                        type: 'select',
                        colSpan: 12, // Full width
                        placeholder: "Select country",
                        showSearch: true, // Enable search functionality
                        options: [], // Keep this empty as options will be dynamic
                        dynamicOptions: { // Configuration for fetching options from an API
                            url: "/Lookup/GetDropdownData", // Your API endpoint for skills (ensure it returns an array of objects like {id: 1, name: "JavaScript"})
                            method: "POST", // HTTP method for the request
                            body: { "dropdowns": ["country"] },
                            // JavaScript function string to transform the API response into { label, value } format
                            transformData: `(apiResponse) => apiResponse.country.map(item => ({ label: ${isArabic ? 'item.NameAr' : 'item.Name'}, value: item.Id }))`,
                        },
                    },
                    {
                        name: 'Nationality', label: 'Nationality', type: 'input', colSpan: 12
                    },
                    {
                        name: 'address', label: 'Full Address', type: 'textarea', required: true, colSpan: 24,
                        pattern: 'AlphaNumericAndSomeSpecialCharEngAr',
                        validation: [
                            { max: 250, message: t('Address cannot be more than 250 characters long.') },
                        ],
                    },
                ],
            },
            {
                key: 'salesPerson-other',
                title: 'Commission',
                type: 'card',
                layout: { type: 'grid', gutter: 24 },
                fields: [
                    {
                        name: 'PremiumNetAmtPercentage',
                        label: 'Premium Net AmtPercentage',
                        type: 'number',
                        required: false,
                        autocomplete: 'false',
                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                        validation: [
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.resolve(); // allow empty
                                    }

                                    if (value < 0 || value > 100) {
                                        return Promise.reject(
                                            t('Premium Net Amt Percentage must be between 0 and 100.')
                                        );
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ],
                    },
                    {
                        name: 'PremiumTotalAmtPercentage',
                        label: 'Premium Total AmtPercentage',
                        type: 'number',
                        required: false,
                        autocomplete: 'false',
                        colSpan: 12, // Takes 12 out of 24 grid units (half width)
                        validation: [
                            {
                                validator: (_, value) => {
                                    if (value === undefined || value === null || value === '') {
                                        return Promise.resolve(); // allow empty
                                    }

                                    if (value < 0 || value > 100) {
                                        return Promise.reject(
                                            t('Premium Total Amt Percentage must be between 0 and 100.')
                                        );
                                    }

                                    return Promise.resolve();
                                },
                            },
                        ],
                    },
                ],
            }
        ],
    };

    return (
        <DynamicForm
            schema={SalesPersonFormSchema}
            submitApiUrl={API_ENDPOINTS.SALESPERSON.SAVE_SALESPERSON}
            getByIdApiUrl={API_ENDPOINTS.SALESPERSON.GET_SALESPERSON_BY_ID}
            updateApiUrl={API_ENDPOINTS.SALESPERSON.UPDATE_SALESPERSON}
            refreshTableDataKey={QUERY_KEYS.SALESPERSON.SALESPERSON}
        />
    );
}
